import { errorText } from '@/api/client';
/**
 * The employee page's Tilgjengelighet tab (issue #70, 2026-10-12): what the person has said about
 * when they can work, from today on. A manager with ansatte.endre adds, changes and deletes an
 * entry in the drawer; while the company asks for approval (Innstillinger → Arbeidsmåte), a
 * pending entry has Godkjenn and Avslå for whoever holds tilgjengelighet.godkjenne.
 *
 * Three kinds, and nothing said is a fourth: available (all day, or only between two times),
 * unavailable (the whole day, as the person's own form records it), and a wish to work then,
 * which limits nothing.
 */
import {
  type ApiAvailability,
  type AvailabilityWrite,
  useAvailabilityAdminWrites,
  useUserAvailabilities,
} from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { oslo, osloISO, today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Drawer, Empty, Skeleton, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { fmtD } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Notice } from '@/ui/page';
import { addDays } from '@timefly/core';
import { useState } from 'react';

export type Kind = 'available' | 'unavailable' | 'preference';
export const KIND_LABEL: Record<Kind, string> = {
  available: 'Tilgjengelig',
  unavailable: 'Ikke tilgjengelig',
  preference: 'Ønsker å jobbe',
};
export const kindOf = (a: Pick<ApiAvailability, 'is_available' | 'is_preference'>): Kind =>
  !a.is_available ? 'unavailable' : a.is_preference ? 'preference' : 'available';

export interface Draft {
  id?: number;
  date: string;
  kind: Kind;
  /** both empty: the whole day */
  from: string;
  to: string;
  weekly: boolean;
  until: string;
  recurring?: boolean;
}

export const newDraft = (day: string): Draft => ({
  date: day,
  kind: 'unavailable',
  from: '',
  to: '',
  weekly: false,
  until: addDays(day, 28),
});

const WHOLE = (from: string, to: string) => from <= '00:00' && to >= '23:59';

export function draftOf(a: ApiAvailability): Draft {
  const s = oslo(a.start_date_time);
  const e = oslo(a.end_date_time);
  const from = s?.time?.slice(0, 5) ?? '';
  const to = e?.time?.slice(0, 5) ?? '';
  const whole = WHOLE(from, to);
  return {
    id: a.id,
    date: s?.date ?? today(),
    kind: kindOf(a),
    from: whole ? '' : from,
    to: whole ? '' : to,
    weekly: false,
    until: '',
    recurring: a.is_recurring,
  };
}

/** What is wrong with the draft, in words, or null. */
export function problem(d: Draft): string | null {
  if (!d.date) return T('Velg en dato.');
  if (d.kind !== 'unavailable' && !!d.from !== !!d.to)
    return T('Fyll inn både fra og til, eller ingen av dem.');
  if (d.kind !== 'unavailable' && d.from && d.to && d.to <= d.from) return T('Til må være etter fra.');
  if (d.weekly && (!d.until || d.until < d.date)) return T('Velg når gjentakelsen slutter.');
  return null;
}

/** The body the API takes; an unavailable day is the whole day, as the person's own form records it. */
export function bodyOf(d: Draft): AvailabilityWrite {
  const part = d.kind !== 'unavailable' && d.from && d.to;
  return {
    start_date_time: osloISO(d.date, part ? d.from : '00:00'),
    end_date_time: osloISO(d.date, part ? d.to : '23:59'),
    is_available: d.kind !== 'unavailable',
    is_preference: d.kind === 'preference',
    ...(d.id == null
      ? d.weekly
        ? {
            is_recurring: true,
            recurrence_pattern: 'weekly' as const,
            recurrence_end_date: osloISO(d.until, '23:59'),
          }
        : { is_recurring: false }
      : {}),
  };
}

const hours = (a: Pick<ApiAvailability, 'start_date_time' | 'end_date_time'>) => {
  const from = oslo(a.start_date_time)?.time?.slice(0, 5) ?? '';
  const to = oslo(a.end_date_time)?.time?.slice(0, 5) ?? '';
  return WHOLE(from, to) ? T('hele dagen') : `${from}–${to}`;
};

/** The earlier approved values a pending or rejected change still stands on, in words, or null. */
export function approvedBefore(a: ApiAvailability): string | null {
  if (!a.approved_start_date_time || !a.approved_end_date_time) return null;
  const kind = kindOf({ is_available: !!a.approved_is_available, is_preference: !!a.approved_is_preference });
  const d = oslo(a.approved_start_date_time)?.date ?? '';
  return `${T('Godkjent før')}: ${T(KIND_LABEL[kind]).toLowerCase()} ${fmtD(d)}, ${hours({ start_date_time: a.approved_start_date_time, end_date_time: a.approved_end_date_time })}`;
}

export function StatusChip({ a }: { a: Pick<ApiAvailability, 'approval_status'> }) {
  if (a.approval_status === 'pending')
    return (
      <Chip tone="warn" small>
        {T('Venter på godkjenning')}
      </Chip>
    );
  if (a.approval_status === 'rejected')
    return (
      <Chip tone="dgr" small>
        {T('Avslått')}
      </Chip>
    );
  // approved, or written while no approval was asked: it stands
  return (
    <Chip tone="ok" small>
      {T('Godkjent')}
    </Chip>
  );
}

export function TilgjengelighetTab({ userId }: { userId: number }) {
  const can = useCan();
  const { tenant } = useTenant();
  const asks = !!tenant?.availability_requires_approval;
  const q = useUserAvailabilities(userId);
  const w = useAvailabilityAdminWrites(userId);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const day0 = today();
  const mayEdit = can('ansatte.endre');
  const mayDecide = asks && can('tilgjengelighet.godkjenne');
  const list = (q.data ?? [])
    .filter((a) => (oslo(a.end_date_time)?.date ?? '') >= day0)
    .sort((a, b) => a.start_date_time.localeCompare(b.start_date_time));
  const waiting = list.filter((a) => a.approval_status === 'pending');
  const run = async (ok: string, f: () => Promise<unknown>) => {
    try {
      await f();
      toast(ok);
      return true;
    } catch (e) {
      toast(errorText(e));
      return false;
    }
  };
  const save = async () => {
    if (!draft) return;
    const p = problem(draft);
    if (p) return setErr(p);
    try {
      if (draft.id != null) await w.update.mutateAsync({ id: draft.id, body: bodyOf(draft) });
      else await w.create.mutateAsync(bodyOf(draft));
      toast(T('Lagret'));
      setDraft(null);
    } catch (e) {
      setErr(errorText(e));
    }
  };
  const remove = async (series: boolean) => {
    if (draft?.id == null) return;
    if (await run(T('Slettet'), () => w.remove.mutateAsync({ id: draft.id as number, series })))
      setDraft(null);
  };
  const busy = w.create.isPending || w.update.isPending || w.remove.isPending;

  return (
    <>
      <div className="vtbar">
        <span className="ovmute">
          {asks
            ? T('Bedriften krever godkjenning av tilgjengelighet.')
            : T('Tilgjengelighet meldes uten godkjenning.')}
        </span>
        <span className="sp" />
        {mayEdit && (
          <Button
            onClick={() => {
              setErr(null);
              setDraft(newDraft(day0));
            }}
          >
            <Icon name="plus" />
            {T('Ny tilgjengelighet')}
          </Button>
        )}
      </div>
      {mayDecide && waiting.length > 0 && (
        <Notice tone="info" title={T('{n} venter på godkjenning', { n: waiting.length })} />
      )}
      <section className="sec">
        <div className="card2" data-testid="availability-admin-list">
          {q.isLoading ? (
            <Skeleton />
          ) : list.length ? (
            <table className="t ftab">
              <thead>
                <tr>
                  <th>{T('Dato')}</th>
                  <th>{T('Tid')}</th>
                  <th>{T('Type')}</th>
                  {asks && <th>{T('Status')}</th>}
                  {mayDecide && <th />}
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const before = asks ? approvedBefore(a) : null;
                  const date = fmtD(oslo(a.start_date_time)?.date ?? '');
                  return (
                    <tr key={a.id} data-testid="availability-row">
                      <td className="mono">
                        {mayEdit ? (
                          <button
                            type="button"
                            className="linkbtn"
                            onClick={() => {
                              setErr(null);
                              setDraft(draftOf(a));
                            }}
                          >
                            {date}
                          </button>
                        ) : (
                          date
                        )}
                        {a.is_recurring && a.recurrence_pattern === 'weekly' ? `, ${T('hver uke')}` : ''}
                      </td>
                      <td>{a.is_available ? hours(a) : T('hele dagen')}</td>
                      <td>{T(KIND_LABEL[kindOf(a)])}</td>
                      {asks && (
                        <td>
                          <StatusChip a={a} />
                          {before && <small className="ovmute"> {before}</small>}
                        </td>
                      )}
                      {mayDecide && (
                        <td>
                          {a.approval_status === 'pending' && (
                            <span className="ans-gaps">
                              <Button
                                small
                                tone="pri"
                                disabled={w.decide.isPending}
                                onClick={() =>
                                  run(T('Godkjent'), () =>
                                    w.decide.mutateAsync({ id: a.id, ok: true, series: a.is_recurring }),
                                  )
                                }
                              >
                                <Icon name="check" />
                                {T('Godkjenn')}
                              </Button>
                              <Button
                                small
                                disabled={w.decide.isPending}
                                onClick={() =>
                                  run(T('Avslått'), () =>
                                    w.decide.mutateAsync({ id: a.id, ok: false, series: a.is_recurring }),
                                  )
                                }
                              >
                                {T('Avslå')}
                              </Button>
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <Empty>{T('Ingenting meldt framover. Uten melding er tilgjengeligheten ukjent.')}</Empty>
          )}
        </div>
      </section>
      <Drawer
        open={!!draft}
        onClose={() => setDraft(null)}
        blockClose={() => busy}
        title={draft?.id != null ? T('Endre tilgjengelighet') : T('Ny tilgjengelighet')}
        footer={
          <>
            {draft?.id != null && (
              <>
                <Button onClick={() => remove(false)} disabled={busy}>
                  <Icon name="trash" />
                  {T('Slett')}
                </Button>
                {draft.recurring && (
                  <Button onClick={() => remove(true)} disabled={busy}>
                    {T('Slett hele serien')}
                  </Button>
                )}
              </>
            )}
            <span className="sp" />
            <Button onClick={() => setDraft(null)} disabled={busy}>
              {T('Avbryt')}
            </Button>
            <Button tone="pri" onClick={save} disabled={busy}>
              {busy ? T('Lagrer…') : T('Lagre')}
            </Button>
          </>
        }
      >
        {draft && (
          <form
            data-testid="availability-editor"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <div className="fld">
              <span className="lbl">{T('Dato')}</span>
              <DateInput
                className="inp"
                aria-label={T('Dato')}
                value={draft.date}
                onValueChange={(value) => setDraft({ ...draft, date: value })}
              />
            </div>
            <div className="fld">
              <span className="lbl">{T('Type')}</span>
              <select
                className="inp"
                aria-label={T('Type')}
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value as Kind })}
              >
                {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
                  <option key={k} value={k}>
                    {T(KIND_LABEL[k])}
                  </option>
                ))}
              </select>
            </div>
            {draft.kind !== 'unavailable' && (
              <div className="fld">
                <span className="lbl">{T('Kun mellom (valgfritt)')}</span>
                <div className="two">
                  <input
                    className="inp"
                    type="time"
                    aria-label={T('Fra')}
                    value={draft.from}
                    onChange={(e) => setDraft({ ...draft, from: e.target.value })}
                  />
                  <input
                    className="inp"
                    type="time"
                    aria-label={T('Til')}
                    value={draft.to}
                    onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                  />
                </div>
              </div>
            )}
            <p className="hint">
              {draft.kind === 'unavailable'
                ? T('Hele dagen blokkeres i vaktplanen.')
                : draft.kind === 'preference'
                  ? T('Et ønske, ikke en begrensning: vaktplanen kan fortsatt sette opp vakter utenfor.')
                  : T('Uten klokkeslett gjelder det hele dagen.')}
            </p>
            {draft.id == null && (
              <div className="fld">
                <label className="lbl">
                  <input
                    type="checkbox"
                    checked={draft.weekly}
                    onChange={(e) => setDraft({ ...draft, weekly: e.target.checked })}
                  />{' '}
                  {T('Gjenta hver uke')}
                </label>
                {draft.weekly && (
                  <DateInput
                    className="inp"
                    aria-label={T('Til og med')}
                    value={draft.until}
                    onValueChange={(value) => setDraft({ ...draft, until: value })}
                  />
                )}
              </div>
            )}
            {asks && <p className="hint">{T('Det du lagrer her, regnes som godkjent.')}</p>}
            {err && (
              <div className="al dgr" role="alert">
                <span>
                  <Icon name="stop" />
                </span>
                <span>{err}</span>
              </div>
            )}
          </form>
        )}
      </Drawer>
    </>
  );
}
